import moment from "moment";
import React, { useEffect, useState } from "react";
import { Modal, ModalBody, ModalFooter } from "reactstrap";

import { useModalSubmit } from "../../../common/useModalSubmit";

import { DateTime } from "../../../components/Forms/DateTime";
import { PlatformSubscriptionCreate } from "../../../services/api/Platform";

const TIERS = ["FREE", "BASIC", "PRO", "UL_TEAM", "ENTERPRISE"];

type Props = {
  isOpen: boolean;
  teamId: string;
  teamName: string;
  onClose: () => void;
  onSubmit: (
    params: Omit<PlatformSubscriptionCreate, "teamId">,
  ) => void | Promise<void>;
};

export const CreatePlatformSubscriptionModal: React.FunctionComponent<
  Props
> = ({ isOpen, teamName, onClose, onSubmit }) => {
  const [tier, setTier] = useState("PRO");
  const [licenses, setLicenses] = useState(1);
  const [isTrial, setIsTrial] = useState(false);
  const [isOneTime, setIsOneTime] = useState(false);
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [stripeCustomerId, setStripeCustomerId] = useState("");
  const [stripeSubscriptionItemId, setStripeSubscriptionItemId] = useState("");
  const [memo, setMemo] = useState("");

  useEffect(() => {
    setTier("PRO");
    setLicenses(1);
    setIsTrial(false);
    setIsOneTime(false);
    setEndDate(undefined);
    setStripeCustomerId("");
    setStripeSubscriptionItemId("");
    setMemo("");
  }, [isOpen]);

  // A trial or one-time purchase with no end date never expires.
  const needsEndDate = (isTrial || isOneTime) && !endDate;
  const endDateInPast = !!endDate && endDate.getTime() <= Date.now();
  const { submitting, error: saveError, submit } = useModalSubmit(isOpen);

  const error = needsEndDate
    ? "A trial or one-time subscription needs an end date"
    : endDateInPast
    ? "The end date must be in the future"
    : !Number.isInteger(licenses) || licenses < 1
    ? "At least one licence is required"
    : "";

  return (
    <Modal isOpen={isOpen} style={{ maxWidth: "640px" }}>
      <div className="modal-header">
        <h5 className="modal-title">Create Subscription</h5>
        <button
          type="button"
          className="close"
          aria-label="Close"
          onClick={onClose}
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <ModalBody>
        <p className="text-muted">
          For team <strong>{teamName}</strong>
        </p>

        <div className="form-group">
          <label>Plan</label>
          <select
            className="form-control"
            value={tier}
            onChange={(e) => setTier(e.currentTarget.value)}
          >
            {TIERS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="create-is-trial"
              checked={isTrial}
              onChange={(e) => setIsTrial(e.currentTarget.checked)}
            />
            <label htmlFor="create-is-trial" className="form-check-label">
              Trial
            </label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="create-is-one-time"
              checked={isOneTime}
              onChange={(e) => setIsOneTime(e.currentTarget.checked)}
            />
            <label htmlFor="create-is-one-time" className="form-check-label">
              One Time
            </label>
          </div>
        </div>

        <div className="form-group">
          <label>Number of Licenses</label>
          <input
            type="number"
            className="form-control"
            value={licenses}
            onChange={(e) => setLicenses(e.currentTarget.valueAsNumber)}
          />
        </div>

        <div className="form-group">
          <label>
            Expiration Date{" "}
            <i>{isTrial || isOneTime ? "(required)" : "(optional)"}</i>
          </label>
          <DateTime
            onChange={(e) => setEndDate(moment(e).toDate())}
            value={endDate}
          />
        </div>

        <div className="form-group">
          <label>
            Stripe Customer ID <i>(optional)</i>
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="cus_..."
            value={stripeCustomerId}
            onChange={(e) => setStripeCustomerId(e.currentTarget.value)}
          />
          <span className="form-text text-muted">
            A customer on its own is not checked against Stripe — that is a plan
            billed manually against a Stripe customer. It is the subscription
            item below that gets verified, and this customer has to be the one
            that item belongs to.
          </span>
        </div>

        <div className="form-group">
          <label>
            Stripe Subscription Item ID <i>(optional, starts with si_)</i>
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="si_..."
            value={stripeSubscriptionItemId}
            onChange={(e) => setStripeSubscriptionItemId(e.currentTarget.value)}
          />
          <span className="form-text text-muted">
            Leave blank for a subscription billed outside Stripe, or billed
            manually against a Stripe customer.
          </span>
        </div>

        <div className="form-group">
          <label>Memo</label>
          <input
            type="text"
            className="form-control"
            placeholder="why this subscription was created"
            value={memo}
            onChange={(e) => setMemo(e.currentTarget.value)}
          />
        </div>

        {error && <div className="alert alert-warning mb-0">{error}</div>}
        {saveError && (
          <div className="alert alert-danger mb-0">{saveError}</div>
        )}
      </ModalBody>
      <ModalFooter>
        <button
          className="btn btn-light"
          disabled={submitting}
          onClick={onClose}
        >
          Close
        </button>
        <button
          className="btn btn-primary"
          disabled={!!error || submitting}
          onClick={() =>
            void submit(() =>
              onSubmit({
                tier,
                licenses,
                isTrial,
                isOneTime,
                endDate: endDate?.toISOString(),
                stripeCustomerId: stripeCustomerId || undefined,
                stripeSubscriptionItemId: stripeSubscriptionItemId || undefined,
                memo: memo || undefined,
              }),
            )
          }
        >
          {submitting ? "Creating…" : "Create"}
        </button>
      </ModalFooter>
    </Modal>
  );
};
